import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Scroll to the top on navigation.
 *
 * A browser restores scroll position on a real document load, but a client-side route
 * change is not one — so without this, following a link from halfway down the blog index
 * lands you halfway down the article, which reads as a broken page.
 *
 * Two deliberate exceptions:
 *  - a hash link is left alone, so in-page anchors work;
 *  - `POP` navigation (back/forward) is left alone, because the browser's own restored
 *    position is the behaviour people expect there.
 */
export function ScrollToTop(): null {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;

    // `instant` rather than the CSS `scroll-behavior: smooth` set globally: animating a
    // full-page scroll on every navigation is slow and slightly nauseating.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}
